import { Col } from 'react-bootstrap';
import Img from 'react-image';
import { getImageForFinanceSource } from '@upstart/harmony/lib/metadata';
import { fmtPercent, fmtPrice } from '@upstart/harmony/lib/formatter';
import React from 'react';
import {
  FinanceResponse,
  Rate,
} from '@upstart/harmony/lib/legos/financingTypes';
import { FinanceProvider } from '@upstart/harmony/lib/constants';
import { isCounter } from '@upstart/harmony/lib/response-utils';
import CounterOfferAdjustmentTable from './CounterOfferAdjustmentTable';
import LoadingOverlay from '../../../components/LoadingOverlay';
import CounterOfferSummaryTable from './CounterOfferSummaryTable';
import OfferTable from './OfferTable';
import OfferFinancingReview from './OfferFinancingReview';

const getCounterOfferNotice = ({
  financeResponse,
  maxAllowableRate,
}: {
  financeResponse?: FinanceResponse;
  maxAllowableRate?: Rate;
}) => {
  const financeAmount = fmtPrice(financeResponse?.data?.financeAmount ?? 0);
  const downPayment = fmtPrice(financeResponse?.data?.downPayment ?? 0);
  const maxFinanceAmount = fmtPrice(maxAllowableRate?.maxFinanceAmount ?? 0);
  const months = maxAllowableRate?.months ?? 'N/A';
  const minDownPaymentAmount = fmtPrice(
    maxAllowableRate?.minDownPaymentAmount ?? 0,
  );

  return `The requested Finance amount of ${financeAmount} with a ${downPayment} down payment is not approved. However, Upstart has approved a finance amount of ${maxFinanceAmount} for ${months ??
    'N/A'} months with a minimum down payment of ${minDownPaymentAmount}. To proceed, please adjust the deal structure and continue.`;
};

const renderCounterNeedsAdjustmentContent = ({
  isEditing,
  fetchingPayments,
  originalResponse,
  maxAllowableRate,
  financeAmount,
  maxAllowableFinanceAmount,
  responseIsAutoAdjustedCounter,
}: {
  isEditing: boolean;
  fetchingPayments: boolean;
  originalResponse?: FinanceResponse;
  maxAllowableFinanceAmount: number;
  maxAllowableRate?: Rate;
  financeAmount: number;
  responseIsAutoAdjustedCounter: boolean;
}) => {
  const counteredResponseHasInvalidBackendOrGap =
    originalResponse?.validations?.isBackendOverLimit ||
    originalResponse?.validations?.isGapPremiumOverLimit ||
    originalResponse?.validations?.isLTVBelowMinimumForGap;
  const showAdjustmentSummaryTable =
    responseIsAutoAdjustedCounter && counteredResponseHasInvalidBackendOrGap;

  return (
    <Col xs={12} sm={isEditing ? 4 : 6} className="offer-section">
      <LoadingOverlay inline={false} active={fetchingPayments}>
        {showAdjustmentSummaryTable ? (
          <CounterOfferAdjustmentTable financeResponse={originalResponse} />
        ) : (
          <>
            <div className="offer-section-title">
              <Img
                className="finance-source-image"
                src={getImageForFinanceSource(FinanceProvider.UPSTART)}
                unloader={
                  <div className="finance-source-name">
                    {FinanceProvider.UPSTART}
                  </div>
                }
              />
            </div>
            <div className="offer-counter-notice">
              <div>
                <i className="offer-counter-notice-icon glyphicon glyphicon-info-sign" />
              </div>
              <div data-testid="CounterOfferNotice">
                {getCounterOfferNotice({
                  financeResponse: originalResponse,
                  maxAllowableRate,
                })}
              </div>
            </div>
            <div className="pt-15" />
            <div className="offer-section-title">Finance Amount</div>
            <div>
              {originalResponse && (
                <CounterOfferSummaryTable
                  originalResponse={originalResponse}
                  maxAllowableFinanceAmount={maxAllowableFinanceAmount}
                  financeAmount={financeAmount}
                  maxAllowableRate={maxAllowableRate}
                />
              )}
            </div>
          </>
        )}
      </LoadingOverlay>
    </Col>
  );
};

export const OfferSummaryContent: React.FC<{
  originalResponse?: FinanceResponse;
  response?: FinanceResponse;
  isEditing: boolean;
  fetchingPayments: boolean;
  responseIsAutoAdjustedCounter: boolean;
  maxAllowableRate?: Rate;
  maxAllowableFinanceAmount: number;
  financeAmount: number;
  isEditable: boolean;
  showDealerFee: boolean;
}> = ({
  originalResponse,
  isEditing,
  responseIsAutoAdjustedCounter,
  fetchingPayments,
  maxAllowableRate,
  maxAllowableFinanceAmount,
  response,
  financeAmount,
  isEditable,
  showDealerFee,
}) => {
  const isNeedsAdjustmentCounter =
    response && isCounter(response) && response?.validations?.isInvalid;
  const validations = isEditing
    ? response?.validations
    : originalResponse?.validations;
  return isNeedsAdjustmentCounter ? (
    renderCounterNeedsAdjustmentContent({
      isEditing,
      fetchingPayments,
      originalResponse,
      maxAllowableFinanceAmount,
      maxAllowableRate,
      financeAmount,
      responseIsAutoAdjustedCounter,
    })
  ) : (
    <>
      <Col
        xs={6}
        sm={isEditing ? 4 : 6}
        className="offer-section"
        data-testid="ValidOfferContent"
      >
        <LoadingOverlay inline={false} active={fetchingPayments}>
          <div className="offer-section-title">
            {responseIsAutoAdjustedCounter
              ? 'Upstart Approved (Counter) Offer'
              : 'Current Offer'}
          </div>
          {!isEditing && (
            <OfferFinancingReview
              response={response}
              validations={validations}
              showDealerFee={showDealerFee}
              isEditable={isEditable}
            />
          )}
          {isEditing && responseIsAutoAdjustedCounter ? (
            <CounterOfferAdjustmentTable financeResponse={originalResponse} />
          ) : (
            <OfferTable
              response={response}
              fetchingPayments={fetchingPayments}
              validations={validations}
            />
          )}
          {!originalResponse?.validations?.isInvalid && isEditing && (
            <div className="offer-original" data-testid="OriginalOfferContent">
              Your Original Offer:{' '}
              {fmtPrice(originalResponse?.data?.monthly ?? 0)}
              /month, {fmtPercent(originalResponse?.data?.apr ?? 0)},{' '}
              {originalResponse?.data?.months} months
            </div>
          )}
        </LoadingOverlay>
      </Col>
      {responseIsAutoAdjustedCounter && !isEditing && (
        <Col xs={6}>
          <CounterOfferAdjustmentTable financeResponse={originalResponse} />
        </Col>
      )}
    </>
  );
};
